import { Card as FcCard } from '../ui';
import { useState, useEffect, useRef } from "react";
import { supabase } from "../lib/supabaseClient";
import { subscribeToChanges } from '../lib/realtimeSubscriptions';
import notify from "../lib/notify";
import { Button as FcButton, Textarea as FcTextarea } from '../ui';

export default function TicketConversation({ ticketId, userId, isAdmin = false, initialMessage = null, createdAt = null }) {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    fetchMessages();
    
    // Set up real-time subscription for new messages
    const unsubscribe = subscribeToChanges(supabase, `ticket_messages_${ticketId}`,
        {
          event: 'INSERT',
          schema: 'public',
          table: 'ticket_messages',
          filter: `ticket_id=eq.${ticketId}`
        },
        (payload) => {
          // Avoid duplicates by checking if message already exists
          setMessages((prev) => {
            const exists = prev.some(msg => msg.id === payload.new.id);
            if (exists) return prev;
            return [...prev, payload.new];
          });
          scrollToBottom();
        }
      );

    return () => {
      unsubscribe();
    };
  }, [ticketId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchMessages = async () => {
    try {
      const { data, error } = await supabase
        .from("ticket_messages")
        .select("*")
        .eq("ticket_id", ticketId)
        .order("created_at", { ascending: true });

      if (error) throw error;
      setMessages(data || []);
    } catch (error) {
      notify("Failed to load conversation", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim()) {
      notify("Please enter a message", "error");
      return;
    }

    setSending(true);
    try {
      const { data, error } = await supabase
        .from("ticket_messages")
        .insert({
          ticket_id: ticketId,
          user_id: userId,
          is_admin: isAdmin,
          message: newMessage.trim(),
        })
        .select()
        .single();

      if (error) throw error;

      // Optimistically add the message to the UI
      if (data) {
        setMessages((prev) => [...prev, data]);
      }

      setNewMessage("");
      notify("Message sent", "success");
    } catch (error) {
      notify("Failed to send message", "error");
    } finally {
      setSending(false);
    }
  };

  const formatMessageTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
      });
    } else {
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="text-content-secondary">Loading conversation...</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Messages Container */}
      <div className="flex-1 overflow-y-auto max-h-96 space-y-4 p-4 bg-gray-50 rounded-lg">
        {/* Initial Message (Ticket Description) */}
        {initialMessage && (
          <div className={`flex ${isAdmin ? 'justify-start' : 'justify-end'}`}>
            <div className="max-w-[70%]">
              <div className={`text-xs font-medium mb-1 ${isAdmin ? 'text-left' : 'text-right'}`}>
                <span className="text-content-primary">You</span>
              </div>
              <FcCard as="div" padding="none" className={`rounded-lg px-4 py-3 ${isAdmin ? "bg-surface-card border-2 border-blue-200 shadow-sm" : "bg-action-primary text-white"}`}>
                <div className={`text-xs mb-1 ${isAdmin ? "text-content-secondary" : 'text-white/70'}`}>
                  Initial Message
                </div>
                <p className={`text-sm whitespace-pre-wrap break-words ${isAdmin ? "text-content-primary" : 'text-white'}`}>
                  {initialMessage}
                </p>
                <div className={`text-xs mt-2 ${isAdmin ? "text-content-secondary" : 'text-white/80'}`}>
                  {formatMessageTime(createdAt)}
                </div>
              </FcCard>
            </div>
          </div>
        )}

        {messages.length === 0 && !initialMessage ? (
          <div className="text-center text-content-secondary py-8">
            No messages yet. Start the conversation!
          </div>
        ) : (
          <>
            {messages.map((message, index) => {
              const isOwnMessage = isAdmin ? message.is_admin : !message.is_admin;
              const showAvatar = index === 0 || messages[index - 1].is_admin !== message.is_admin;

              return (
                <div
                  key={message.id}
                  className={`flex ${isOwnMessage ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[70%] ${
                      isOwnMessage ? "order-2" : "order-1"
                    }`}
                  >
                    {showAvatar && (
                      <div
                        className={`text-xs font-medium mb-1 ${
                          isOwnMessage ? "text-right" : "text-left"
                        }`}
                      >
                        <span className={message.is_admin ? "text-ui-green" : "text-content-primary"}>
                          {message.is_admin ? "Support Team" : "You"}
                        </span>
                      </div>
                    )}
                    <div
                      className={`rounded-lg px-4 py-2 ${
                        message.is_admin
                          ? "bg-blue-100 text-content-primary"
                          : "bg-action-primary text-white"
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap break-words">{message.message}</p>
                      <p
                        className={`text-xs mt-1 ${
                          message.is_admin ? "text-content-secondary" : "text-white/80"
                        }`}
                      >
                        {formatMessageTime(message.created_at)}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Reply Form */}
      <form onSubmit={handleSendMessage} className="mt-4">
        <div className="flex gap-2">
          <FcTextarea aria-label="new Message"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder={isAdmin ? "Type your response..." : "Type your reply..."}
            className="flex-1 border focus:border-transparent resize-none"
            rows={3}
            disabled={sending}
          />
          <FcButton variant="tertiary"
            type="submit"
            disabled={sending || !newMessage.trim()}
            className={`self-end ${
              sending || !newMessage.trim()
                ? "cursor-not-allowed"
                : ""
            }`}
          >
            {sending ? "Sending..." : "Send"}
          </FcButton>
        </div>
      </form>
    </div>
  );
}
